import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Code2, Terminal, BookOpen, Webhook, KeyRound, Rocket, Shield, FlaskConical,
  Download, ArrowRight, Check, Copy, Github, Package, Globe,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_META = {
  python: { icon: '🐍', name: 'Python', install: 'pip install -e .', hint: 'requests ≥ 2.28 · Python ≥ 3.8' },
  nodejs: { icon: '⬢',  name: 'Node.js', install: 'npm install', hint: 'axios ≥ 1.6 · Node ≥ 16' },
  php:    { icon: '🐘', name: 'PHP',     install: 'composer install', hint: 'Guzzle 7 · PHP ≥ 7.4' },
};

const SAMPLES = {
  python: `from surplify import SurplifyClient

client = SurplifyClient(api_token="sfy_live_...")
materials = client.list_materials(limit=10)
for m in materials["data"]:
    print(m["external_ref"], m["quantity"])`,
  nodejs: `const { SurplifyClient } = require("@surplify/sdk");

const client = new SurplifyClient({ apiToken: "sfy_live_..." });
const { data } = await client.listMaterials({ limit: 10 });
data.forEach(m => console.log(m.external_ref, m.quantity));`,
  php: `use Surplify\\SurplifyClient;

$client = new SurplifyClient(["api_token" => "sfy_live_..."]);
$materials = $client->listMaterials(10);
foreach ($materials["data"] as $m) {
    echo $m["external_ref"] . " · " . $m["quantity"] . "\\n";
}`,
};

export default function DevelopersPage() {
  const [sdks, setSdks] = useState([]);
  const [copied, setCopied] = useState(null);
  const [activeLang, setActiveLang] = useState('python');
  const [events, setEvents] = useState([]);

  useEffect(() => {
    axios.get(`${API}/v1/sdks`).then(r => setSdks(r.data?.sdks || [])).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/v1/openapi.json`).then(() => {}).catch(() => {});
  }, []);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success('Kopiert');
      setTimeout(() => setCopied(null), 1500);
    } catch { /* noop */ }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="developers-page">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-background dark:to-slate-950">
        <div className="container mx-auto px-4 py-20 md:py-28">
          <div className="max-w-3xl">
            <Badge className="bg-orange-100 text-orange-700 mb-5 font-mono" data-testid="developers-badge">
              Surplify Developer Platform · v1.0
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
              Integriere den B2B-Verpackungsmarktplatz<br />
              <span className="text-orange-500">in wenigen Minuten.</span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mt-6 leading-relaxed">
              REST-API mit OpenAPI&nbsp;3.1, OAuth&nbsp;2.0 Client-Credentials, HMAC-signierten Webhooks und
              offiziellen SDKs für Python, Node.js und PHP. Sandbox mit Demo-Daten in 1 Klick.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="/api/v1/docs" target="_blank" rel="noreferrer">
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600" data-testid="developers-open-docs-btn">
                  <BookOpen className="h-5 w-5 mr-2" /> Swagger UI öffnen
                </Button>
              </a>
              <Link to="/seller-portal?tab=integration">
                <Button size="lg" variant="outline" data-testid="developers-sandbox-btn">
                  <FlaskConical className="h-5 w-5 mr-2" /> Sandbox erzeugen
                </Button>
              </Link>
              <a href="#sdks">
                <Button size="lg" variant="ghost" data-testid="developers-scroll-sdks-btn">
                  <Download className="h-5 w-5 mr-2" /> SDK herunterladen
                </Button>
              </a>
            </div>

            <div className="flex flex-wrap gap-6 mt-10 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Kein Rate-Limit für Standard-Reads</div>
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> IP-Allowlisting & mTLS</div>
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> 99.9 % Uptime</div>
            </div>
          </div>
        </div>
        <div className="pointer-events-none absolute -right-40 -top-40 w-[520px] h-[520px] bg-orange-500/10 rounded-full blur-3xl" />
      </section>

      {/* Features */}
      <section className="container mx-auto px-4 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FeatureCard icon={KeyRound} title="OAuth 2.0 + Bearer-Token" description="Langlebige Site-Tokens oder kurzlebige JWTs via Client-Credentials." />
          <FeatureCard icon={Webhook} title="Outbound Webhooks" description="HMAC-signierte Events wie order.created, product.updated — mit Auto-Retry." />
          <FeatureCard icon={FlaskConical} title="Isolierte Sandbox" description="Separate Collections, Demo-Daten, 1-Klick-Provisioning. Null Risiko für Echtdaten." />
          <FeatureCard icon={Shield} title="Enterprise Security" description="IP-Allowlisting pro Token, mTLS-Zertifikate, Audit-Log aller Aktionen." />
        </div>
      </section>

      {/* Quickstart */}
      <section className="border-y bg-slate-50/60 dark:bg-slate-950/50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl md:text-4xl font-bold">Quickstart in 3 Schritten</h2>
            <p className="text-muted-foreground mt-3">
              Vom Portal-Token bis zum ersten erfolgreichen API-Call — keine Registrierung, kein Enterprise-Sales erforderlich.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StepCard
              step="1"
              title="Token im Portal erstellen"
              description={'Login → Seller Portal → Tab „Schnittstelle". Scopes wählen, Token einmalig sichern.'}
              cta={<Link to="/seller-portal?tab=integration"><Button size="sm" variant="outline">Portal öffnen<ArrowRight className="h-3.5 w-3.5 ml-2" /></Button></Link>}
            />
            <StepCard
              step="2"
              title="SDK installieren"
              description="Python, Node.js oder PHP — als ZIP herunterladen und lokal installieren."
              cta={<a href="#sdks"><Button size="sm" variant="outline">Zu den SDKs<ArrowRight className="h-3.5 w-3.5 ml-2" /></Button></a>}
            />
            <StepCard
              step="3"
              title="Loslegen"
              description="Swagger-UI mit Try-it-out, Sandbox mit 5 Materialien vorgefüllt, Webhooks testen."
              cta={<a href="/api/v1/docs" target="_blank" rel="noreferrer"><Button size="sm" variant="outline">Docs<ArrowRight className="h-3.5 w-3.5 ml-2" /></Button></a>}
            />
          </div>
        </div>
      </section>

      {/* SDKs */}
      <section id="sdks" className="container mx-auto px-4 py-20">
        <div className="max-w-3xl mb-10">
          <h2 className="text-3xl md:text-4xl font-bold flex items-center gap-3">
            <Package className="h-8 w-8 text-orange-500" /> Offizielle Client-Libraries
          </h2>
          <p className="text-muted-foreground mt-3">
            Alle SDKs inkl. OAuth-Flow, Retry mit Backoff, Webhook-Signatur-Verifikation und TypeScript-freundlicher
            Methoden-Signatur. Version <code className="font-mono text-xs">{sdks.length ? '1.0.0' : '…'}</code>.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {['python', 'nodejs', 'php'].map(lang => {
            const sdk = sdks.find(s => s.lang === lang);
            const meta = LANG_META[lang];
            return (
              <Card key={lang} className={`cursor-pointer transition hover:shadow-lg ${activeLang === lang ? 'ring-2 ring-orange-500' : ''}`} onClick={() => setActiveLang(lang)} data-testid={`sdk-card-${lang}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl" aria-hidden>{meta.icon}</span>
                      <div>
                        <CardTitle>{meta.name}</CardTitle>
                        <CardDescription className="text-xs mt-0.5">{meta.hint}</CardDescription>
                      </div>
                    </div>
                    <a
                      href={`${API}/v1/sdks/${lang}`}
                      onClick={(e) => e.stopPropagation()}
                      download
                      data-testid={`sdk-download-${lang}`}
                    >
                      <Button size="sm" className="bg-orange-500 hover:bg-orange-600">
                        <Download className="h-4 w-4 mr-1" /> ZIP
                      </Button>
                    </a>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-950 text-slate-100 p-3 rounded-md font-mono text-xs">
                    $ {meta.install}
                  </div>
                  {sdk && (
                    <p className="text-[11px] text-muted-foreground mt-2 font-mono">{sdk.filename}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Code sample */}
        <Card data-testid="sdk-sample-card">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg"><Terminal className="h-5 w-5 text-orange-500" /> Beispiel: Materialien lesen</CardTitle>
              <CardDescription className="mt-1">Sprache wählen durch Klick auf die Karten oben</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => copy(SAMPLES[activeLang], `sample-${activeLang}`)} data-testid={`sdk-sample-copy-${activeLang}`}>
              {copied === `sample-${activeLang}` ? <Check className="h-4 w-4 mr-1 text-emerald-500" /> : <Copy className="h-4 w-4 mr-1" />}
              Code kopieren
            </Button>
          </CardHeader>
          <CardContent>
            <pre className="bg-slate-950 text-slate-100 text-xs p-4 rounded-lg overflow-x-auto" data-testid={`sdk-sample-${activeLang}`}>
              <code>{SAMPLES[activeLang]}</code>
            </pre>
          </CardContent>
        </Card>

        {/* CLI Scaffolder */}
        <Card className="mt-10 border-orange-200/70 dark:border-orange-900/60" data-testid="cli-scaffolder-card">
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Rocket className="h-5 w-5 text-orange-500" />
                  Zero-Code Bootstrapping: <code className="font-mono text-sm bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded">create-surplify-app</code>
                </CardTitle>
                <CardDescription className="mt-2">
                  Ein Command, und du hast ein lauffähiges Projekt mit Token-Config, Hello-World API-Call
                  und Webhook-Receiver inkl. HMAC-Verifikation.
                </CardDescription>
              </div>
              <a
                href={`${API}/v1/cli/create-surplify-app`}
                download
                data-testid="cli-download-btn"
              >
                <Button size="lg" className="bg-orange-500 hover:bg-orange-600">
                  <Download className="h-4 w-4 mr-2" /> CLI herunterladen (ZIP)
                </Button>
              </a>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-950 text-slate-100 text-xs font-mono p-4 rounded-lg overflow-x-auto">
              <div className="text-slate-500"># ZIP entpacken, dann global registrieren:</div>
              <div>$ unzip create-surplify-app-1.0.0.zip && cd create-surplify-app && npm link</div>
              <div className="mt-2 text-slate-500"># Danach ein neues Projekt scaffolden:</div>
              <div>$ create-surplify-app my-integration</div>
              <div className="mt-2 text-slate-500"># Installieren & starten:</div>
              <div>$ cd my-integration && npm install && npm run hello</div>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> .env mit Token &amp; Webhook-Secret</div>
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Express-Webhook-Receiver</div>
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> HMAC-SHA256 timingSafeEqual</div>
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500" /> Node ≥ 18 · Null Dependencies außer Express</div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Webhooks */}
      <section className="border-y bg-gradient-to-b from-orange-50/40 via-white to-white dark:from-orange-950/20 dark:via-background dark:to-background py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold flex items-center gap-3">
                <Webhook className="h-8 w-8 text-orange-500" /> Webhooks
              </h2>
              <p className="text-muted-foreground mt-3">
                Subscribe Deine Systeme auf Lifecycle-Events. Wir senden HMAC-signierte HTTP-Requests
                an Deine URL, mit automatischen Retries (1m → 5m → 30m → 2h → 6h) bei Fehlschlägen.
              </p>
              <div className="mt-6 space-y-2">
                {[
                  ['order.*',         'Neue Bestellung, Status-Wechsel, Stornierung'],
                  ['product.*',       'Material angelegt / aktualisiert / ausverkauft'],
                  ['subscription.*',  'Abo angelegt, erneuert, gekündigt'],
                  ['commission.*',    'Provision berechnet oder ausbezahlt'],
                  ['user.*',          'User-Lifecycle: created, updated, invited'],
                ].map(([e, d]) => (
                  <div key={e} className="flex items-start gap-3 text-sm">
                    <code className="font-mono text-orange-600 min-w-[140px]">{e}</code>
                    <span className="text-muted-foreground">{d}</span>
                  </div>
                ))}
              </div>
              <Link to="/seller-portal?tab=integration" className="inline-block mt-6">
                <Button variant="outline" data-testid="developers-open-webhooks-btn">
                  Webhooks konfigurieren <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div>
              <p className="text-xs font-mono text-muted-foreground mb-2">Beispiel-Request:</p>
              <pre className="bg-slate-950 text-slate-100 text-xs p-4 rounded-lg overflow-x-auto">
{`POST /your-webhook-url HTTP/1.1
Host: api.ihre-firma.de
Content-Type: application/json
User-Agent: Surplify-Webhooks/1.0
X-Surplify-Event: order.created
X-Surplify-Delivery: dlv_abc123...
X-Surplify-Timestamp: 1713393600
X-Surplify-Signature: t=1713393600,v1=9f86...

{
  "id": "evt_01hxy8",
  "event": "order.created",
  "created_at": "2026-04-18T07:20:00Z",
  "site_id": "site_abc",
  "data": {
    "order_id": "ord_xyz",
    "status": "accepted",
    ...
  }
}`}
              </pre>
              <p className="text-xs text-muted-foreground mt-3">
                Signatur-Verifikation ist in allen SDKs eingebaut. Siehe <code>verify_webhook_signature</code>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="container mx-auto px-4 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Ressourcen</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <LinkCard href="/api/v1/docs" target="_blank" icon={BookOpen} title="Swagger UI (Try-it-out)" subtitle="/api/v1/docs" />
          <LinkCard href="/api/v1/redoc" target="_blank" icon={BookOpen} title="ReDoc Reference" subtitle="/api/v1/redoc" />
          <LinkCard href="/api/v1/openapi.json" target="_blank" icon={Code2} title="OpenAPI 3.1 JSON" subtitle="Spec herunterladen" />
          <LinkCard href="/seller-portal?tab=integration" icon={FlaskConical} title="Sandbox-Umgebung" subtitle="1-Klick Provisionierung" />
          <LinkCard href="mailto:support@surplify.de" icon={Globe} title="Developer Support" subtitle="support@surplify.de" />
          <LinkCard href="/api/v1" target="_blank" icon={Rocket} title="API v1 Root" subtitle="Discovery-Index" />
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-slate-950 text-slate-50">
        <div className="container mx-auto px-4 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold">Bereit loszulegen?</h2>
          <p className="text-slate-300 mt-3 max-w-xl mx-auto">
            Erzeuge Deinen ersten Sandbox-Token und teste die API in 60 Sekunden.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/seller-portal?tab=integration">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600" data-testid="developers-footer-cta">
                <FlaskConical className="h-5 w-5 mr-2" /> Sandbox starten
              </Button>
            </Link>
            <a href="/api/v1/docs" target="_blank" rel="noreferrer">
              <Button size="lg" variant="outline" className="text-slate-900">
                Docs öffnen
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─── Building blocks ──────────────────────────────────────────────

function FeatureCard({ icon: Icon, title, description }) {
  return (
    <Card className="border-2 hover:border-orange-300 transition" data-testid={`feature-${title.replace(/\s/g, '-').toLowerCase()}`}>
      <CardHeader className="pb-3">
        <div className="h-10 w-10 rounded-lg bg-orange-50 dark:bg-orange-950 flex items-center justify-center mb-2">
          <Icon className="h-5 w-5 text-orange-600" />
        </div>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
      </CardContent>
    </Card>
  );
}

function StepCard({ step, title, description, cta }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">{step}</div>
          <CardTitle>{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">{description}</p>
        {cta}
      </CardContent>
    </Card>
  );
}

function LinkCard({ href, target, icon: Icon, title, subtitle }) {
  return (
    <a href={href} target={target} rel={target ? 'noreferrer' : undefined} className="block group">
      <Card className="hover:shadow-md transition group-hover:border-orange-300">
        <CardContent className="pt-5 pb-4 px-5 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-orange-50 dark:bg-orange-950 flex items-center justify-center flex-shrink-0">
            <Icon className="h-5 w-5 text-orange-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{title}</p>
            <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-orange-500" />
        </CardContent>
      </Card>
    </a>
  );
}
